// src/features/rrhh/horarios/components/DuplicarSemanaAnteriorDialog.jsx
import { useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";

export default function DuplicarSemanaAnteriorDialog({
  open,
  onClose,
  empleados = [],
  areas = [],
  semanaInicioLabel = "",
  scope,
  onScope,
  empleadoIds = [],
  onEmpleadoIds,
  areaId,
  onAreaId,
  loading = false,
  onConfirm,
}) {
  const weekEmptyByEmpId = useMemo(() => {
    const m = new Map();
    for (const e of empleados || []) {
      const id = Number(e?.employeeId);
      if (!id) continue;
      const hasShifts = Array.isArray(e.shifts) && e.shifts.length > 0;
      m.set(id, !hasShifts);
    }
    return m;
  }, [empleados]);

  const empAreaMap = useMemo(() => {
    const m = new Map();
    for (const e of empleados || []) {
      const eid = Number(e?.employeeId);
      if (!eid) continue;
      const firstShift = (e.shifts || [])[0];
      m.set(eid, firstShift ? Number(firstShift.areaId) : null);
    }
    return m;
  }, [empleados]);

  const weekEmptyByAreaId = useMemo(() => {
    const areaToEmp = new Map();

    for (const e of empleados || []) {
      const eid = Number(e?.employeeId);
      const aid = empAreaMap.get(eid);
      if (!aid || !eid) continue;

      if (!areaToEmp.has(aid)) areaToEmp.set(aid, []);
      areaToEmp.get(aid).push(eid);
    }

    const out = new Map();
    for (const [aid, eids] of areaToEmp.entries()) {
      if (!eids?.length) {
        out.set(aid, false);
        continue;
      }
      const ok = eids.every((eid) => weekEmptyByEmpId.get(eid) === true);
      out.set(aid, ok);
    }
    return out;
  }, [empleados, empAreaMap, weekEmptyByEmpId]);

  const empleadoOptions = useMemo(() => {
    return (empleados || []).map((e) => ({
      id: Number(e.employeeId),
      label: e?.fullName ? `${e.fullName}` : `Empleado #${e.employeeId}`,
      raw: e,
      empty: weekEmptyByEmpId.get(Number(e.employeeId)) === true,
    }));
  }, [empleados, weekEmptyByEmpId]);

  const eligibleEmpleadoOptions = useMemo(() => {
    return empleadoOptions.filter((o) => o.empty === true);
  }, [empleadoOptions]);

  const selectedIdSet = useMemo(() => {
    return new Set((empleadoIds || []).map(Number));
  }, [empleadoIds]);

  const allSelected = useMemo(() => {
    if (!eligibleEmpleadoOptions.length) return false;
    return (
      eligibleEmpleadoOptions.every((o) => selectedIdSet.has(o.id)) &&
      eligibleEmpleadoOptions.length === selectedIdSet.size
    );
  }, [eligibleEmpleadoOptions, selectedIdSet]);

  const selectedEmpleados = useMemo(() => {
    return empleadoOptions.filter((o) => selectedIdSet.has(o.id));
  }, [empleadoOptions, selectedIdSet]);

  const allOption = useMemo(
    () => ({
      id: "__ALL__",
      label: `Todos los disponibles (${eligibleEmpleadoOptions.length})`,
      isAll: true,
    }),
    [eligibleEmpleadoOptions.length],
  );

  const areaOptions = useMemo(() => {
    return (areas || []).map((a) => ({
      id: Number(a.id),
      label: a?.name ? `${a.name}` : `Area #${a.id}`,
      raw: a,
      empty: weekEmptyByAreaId.get(Number(a.id)) === true,
    }));
  }, [areas, weekEmptyByAreaId]);

  const selectedArea = areaOptions.find((o) => o.id === Number(areaId)) || null;

  const needsPick =
    scope === "empleado"
      ? !(empleadoIds || []).length
      : !areaId;

  const warningText =
    scope === "empleado"
      ? needsPick
        ? "Seleccioná uno o más empleados."
        : null
      : needsPick
        ? "Seleccioná un área."
        : selectedArea?.empty
          ? null
          : "La semana NO está vacía para esa área.";

  const canConfirm =
    scope === "empleado"
      ? (empleadoIds || []).length > 0 &&
      (empleadoIds || []).every((id) => weekEmptyByEmpId.get(Number(id)) === true)
      : !needsPick && selectedArea?.empty === true;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: "22px",
          bgcolor: "neutral.50",
          boxShadow: "0 18px 50px rgba(0,0,0,.18)",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack spacing={0.4}>
          <Typography sx={{ fontSize: 18, fontWeight: 800, color: "primary.900" }}>
            Duplicar semana anterior
          </Typography>
          <Typography sx={{ fontSize: 12.5, opacity: 0.8 }}>
            Semana destino (lunes): <b>{semanaInicioLabel}</b>
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {warningText ? (
          <Alert severity={needsPick ? "info" : "warning"} sx={{ borderRadius: "16px", mb: 1.2 }}>
            <b>{warningText}</b>
          </Alert>
        ) : null}

        <Box
          sx={{
            p: 1.2,
            borderRadius: "18px",
            bgcolor: "neutral.100",
            boxShadow: "inset 0 0 2px rgba(0,0,0,.18)",
          }}
        >
          <FormControl>
            <Typography sx={{ fontSize: 13, fontWeight: 800, color: "primary.900", mb: 0.6 }}>
              Objetivo
            </Typography>
          </FormControl>

          <Box sx={{ mt: 1 }}>
            <>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={[allOption, ...empleadoOptions]}
                value={selectedEmpleados}
                onChange={(_, newValue, _reason, details) => {
                  if (details?.option?.isAll) {
                    onEmpleadoIds?.(
                      allSelected ? [] : eligibleEmpleadoOptions.map((o) => o.id),
                    );
                    return;
                  }

                  onEmpleadoIds?.(
                    (newValue || [])
                      .filter((o) => !o.isAll)
                      .map((o) => o.id),
                  );
                }}
                getOptionLabel={(o) => o?.label || ""}
                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                getOptionDisabled={(o) =>
                  o?.isAll ? eligibleEmpleadoOptions.length === 0 : o?.empty !== true
                }
                renderTags={(value, getTagProps) => {
                  const visible = value.slice(0, 3);
                  const hiddenCount = value.length - visible.length;

                  return [
                    ...visible.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        label={option.label}
                        size="small"
                        sx={{
                          maxWidth: 165,
                          "& .MuiChip-label": {
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          },
                        }}
                      />
                    )),
                    ...(hiddenCount > 0
                      ? [
                        <Chip
                          key="more-selected"
                          label={`${hiddenCount} más...`}
                          size="small"
                          sx={{
                            bgcolor: "neutral.200",
                            color: "primary.900",
                            fontWeight: 700,
                          }}
                        />,
                      ]
                      : []),
                  ];
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Empleados (en la tabla)"
                    placeholder="Elegí uno o más empleados"
                    size="small"
                  />
                )}
                renderOption={(props, option) => {
                  const checked = option?.isAll
                    ? allSelected
                    : selectedIdSet.has(option.id);

                  return (
                    <Box
                      component="li"
                      {...props}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                        gap: 1,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Checkbox checked={checked} size="small" />
                        <span>{option.label}</span>
                      </Box>

                      <span style={{ opacity: 0.7, fontSize: 12 }}>
                        {option?.isAll
                          ? `${eligibleEmpleadoOptions.length} disponibles`
                          : option.empty
                            ? "Semana vacía"
                            : "Tiene turnos"}
                      </span>
                    </Box>
                  );
                }}
              />

              <Typography sx={{ fontSize: 12, opacity: 0.75, mt: 0.9, pt:0.5 }}>
                * “Todos los disponibles” selecciona solo empleados con semana destino vacía.
              </Typography>
            </>
            <Typography sx={{ fontSize: 12, opacity: 0.75, mt: 0.9 }}>
              * Solo se puede duplicar si la semana destino está vacía.
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>

        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={!canConfirm || loading}
          sx={{
            borderRadius: "14px",
            fontWeight: 900,
          }}
        >
          {loading ? "Duplicando..." : "Duplicar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}